import React, {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CheckboxTable from 'src/products/connector/pages/add-connector/panels/Destination/CheckboxTable';
import {
  ButtonContainer,
  ConnectorFieldsContainer,
  Description,
  DestinationEditConfigurationTablesContainer,
  ModelNameContainer,
  ModelTablesContainer,
  SourceNameContainer,
  Title,
} from './styles';
import { Button } from '@cherre-frontend/ui';
import { buildTableModelsSections } from './utils/buildTableModelsSections';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { preconfiguredModelsQuery } from 'src/services/DOM/preconfiguredModels/__generated__/preconfiguredModelsQuery.graphql';
import { PreConfiguredModelsQuery } from 'src/services/DOM/preconfiguredModels';
import { updateModelMutation } from 'src/services/DOM/mutations/updateModel/__generated__/updateModelMutation.graphql';
import { UpdateModelMutation } from 'src/services/DOM/mutations/updateModel';
import { useConnectorOverviewUI } from '../../../contexts/ConnectorOverviewUI';
import { DestinationEditConfigurationTablesProps } from './model';
import { BuildTableModelsSectionsReturn } from './utils/buildTableModelsSections/model';
import { CircularProgress } from '@material-ui/core';

const DestinationEditConfigurationTables: FC<
  DestinationEditConfigurationTablesProps
> = ({ connector }): ReactElement => {
  const { preconfiguredModels } = useLazyLoadQuery<preconfiguredModelsQuery>(
    PreConfiguredModelsQuery,
    {}
  );
  const [connectorOverviewUI, setConnectorOverviewUI] =
    useConnectorOverviewUI();
  const [updateModel, updatingModel] =
    useMutation<updateModelMutation>(UpdateModelMutation);
  const form = useRef({});
  const cacheMapperStatus = useRef({});
  const [requestMapperStatus, setRequestMapperStatus] = useState({});

  const modelSections = useMemo<BuildTableModelsSectionsReturn[]>(
    () =>
      buildTableModelsSections({
        connector,
        preconfiguredModels,
        destinationName: connectorOverviewUI.data?.destinationName as string,
      }),
    [connectorOverviewUI, preconfiguredModels]
  );

  useEffect(() => {
    const keys = Object.keys(requestMapperStatus);
    const allPassed =
      keys.length > 0 ? keys.every((key) => requestMapperStatus[key]) : false;

    if (!allPassed) {
      return;
    }

    setConnectorOverviewUI({
      connectorComponent: 'destinations',
      data: {
        renderRevisionBanner: true,
        action: undefined,
      },
    });
  }, [requestMapperStatus]);

  const handleCancelAction = useCallback(() => {
    setConnectorOverviewUI({ connectorComponent: undefined, data: undefined });
  }, []);

  const handleOnChangeForm = useCallback((model, data) => {
    form.current[model.modelName] = {
      form: data.form,
      stableVersion: model.modelStableVersion,
    };
  }, []);

  const handleSaveSelection = useCallback(() => {
    Object.keys(form.current).forEach((modelName) => {
      const tablesForm = form.current[modelName].form;
      cacheMapperStatus.current[modelName] = false;
      const revisionNumber = form.current[modelName].stableVersion;
      const tables = Object.keys(tablesForm).filter(
        (table) => tablesForm[table]
      );

      updateModel({
        variables: {
          modelName,
          egressedTables: [
            {
              revisionNumber,
              tables,
            },
          ],
        },
        onCompleted: () => {
          cacheMapperStatus.current[modelName] = true;
          setRequestMapperStatus({ ...cacheMapperStatus.current });
        },
      });
    });
  }, []);

  return (
    <DestinationEditConfigurationTablesContainer>
      {modelSections.map((model, index) => (
        <ModelTablesContainer key={model.modelName}>
          {index === 0 && (
            <div>
              <Title>Select tables to include:</Title>
              <Description>
                Note: Selecting ‘Select all’ will include all tables listed
                below for egress to this destination, along with any future
                tables added to the connector automatically.
              </Description>
            </div>
          )}
          <ConnectorFieldsContainer>
            <SourceNameContainer>
              Source: {model.sourceDisplayName}
            </SourceNameContainer>
            <ModelNameContainer>
              Model: {model.modelDisplayName}
            </ModelNameContainer>
          </ConnectorFieldsContainer>

          <CheckboxTable
            checkboxForm={model.checkboxForm}
            options={model.options}
            selectedAll={model.selectedAll}
            onChangeForm={(data: any): void => handleOnChangeForm(model, data)}
          />
        </ModelTablesContainer>
      ))}

      <ButtonContainer>
        <Button onClick={handleCancelAction}>Cancel</Button>
        <Button
          disabled={updatingModel}
          onClick={handleSaveSelection}
          variant='contained'
        >
          {updatingModel ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </ButtonContainer>
    </DestinationEditConfigurationTablesContainer>
  );
};

export default DestinationEditConfigurationTables;
